import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PhoneNumberAddModal } from '@pages/Profile/PhoneNumbers/modals/PhoneNumberAdd.modal'
import { PhoneNumberDeleteModal } from '@pages/Profile/PhoneNumbers/modals/PhoneNumberDelete.modal'
import { PhoneNumberVerifyModal } from '@pages/Profile/PhoneNumbers/modals/PhoneNumberVerify.modal'

import { useAccountReadContext, useAccountWriteContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'
import { useSnackbar } from '@context/SnackbarContext'

import { Body1 } from '@ui/Typography'
import { PageSection } from '@ui/components/PageSection'
import { CirclePlusIcon } from '@ui/icons/CirclePlus.icon'

import { ResponseError, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'

import {
  AccountDetailedDto,
  ClientPhoneNumberDto,
  PhoneNumberAddValues,
} from '@model/AccountDetailedDto'
import { isAccountActivatedStatus } from '@model/AccountDetailedStatus'
import { AccountType } from '@model/AccountType'
import { PhoneVerificationWorkflowEnum } from '@model/CreatePhoneNumberVerification'

import { ScrollToIds, useScrollToElementIds } from '@hooks/useScrollToElementIds'
import { useScrollToTop } from '@hooks/useScrollToTop'

import { Loading } from '@shared/components/Loading/Loading'

import { PhoneNumbersCard } from './PhoneNumbersCard'
import { PhoneNumbersTable } from './PhoneNumbersTable'

export const filterIndividualPhoneNumbers = (account: AccountDetailedDto): ClientPhoneNumberDto[] =>
  account.phoneNumbers.filter(
    (phoneNumber) => account.type.id === AccountType.Individual || phoneNumber.type === 'Personal'
  )

export const PhoneNumbersPage: React.FC = () => {
  useScrollToTop()
  const { t } = useTranslation()
  const { scrollIntoView } = useScrollToElementIds()
  const isMobile = useWindowResize()
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const { addSnackbar } = useSnackbar()
  const [isLoading, setIsLoading] = useState(false)

  const locale = useSessionLanguage()
  const { account } = useAccountReadContext()
  const { refreshAccount } = useAccountWriteContext()

  const [, setRequestId] = useState<string>()
  const [verificationSuccess, setVerificationSuccess] = useState(false)
  const shouldCancelVerificationRef = useRef(true)

  const addPhoneModal = useModal(PhoneNumberAddModal)
  const verifyModal = useModal(PhoneNumberVerifyModal)
  const deleteModal = useModal(PhoneNumberDeleteModal)

  const individualPhoneNumbers = useMemo(
    () => (account ? filterIndividualPhoneNumbers(account) : []),
    [account]
  )

  useEffect(() => {
    scrollIntoView([ScrollToIds.ProfilePhoneNumbersHeader])
  }, [individualPhoneNumbers.length, isLoading])

  const handleCloseVerifyModal = () => {
    setRequestId(undefined)
    setVerificationSuccess(false)
    verifyModal.close()
  }

  const handlePhoneError = (error: ResponseError) => {
    const errorCode = error?.response?.response?.data.code

    if (
      errorCode === 'max_new_verification_code_requests_reached' ||
      errorCode === 'phone_verification_max_attempts_reached'
    ) {
      setTimeout(() => {
        refreshAccount(locale)
        handleCloseVerifyModal()
        addSnackbar.error({ message: t("errors.You've reached the maximum verification attempts") })
      }, 1000)
    }
  }

  const resendCode = async (
    phoneNumber: string,
    phoneNumberId: string,
    currentRequestId: string
  ) => {
    if (!currentRequestId) {
      return
    }
    try {
      const phoneVerificationId = await apiClient.resendPhoneNumberVerification(
        currentRequestId,
        phoneNumberId,
        PhoneVerificationWorkflowEnum.WorkflowSMS
      )
      setRequestId(phoneVerificationId.phoneVerificationId)
      addSnackbar.success({
        message: t('Sign up.Code was sent', { phoneNumber }),
      })
    } catch (error: unknown) {
      handlePhoneError(error as ResponseError)
    }
  }

  const onReceivePhoneCall = async (
    phoneNumber: string,
    phoneNumberId: string,
    currentRequestId: string
  ) => {
    if (!currentRequestId) {
      return
    }
    try {
      const phoneVerificationId = await apiClient.resendPhoneNumberVerification(
        currentRequestId,
        phoneNumberId,
        PhoneVerificationWorkflowEnum.WorkFlowTTS
      )
      setRequestId(phoneVerificationId.phoneVerificationId)
      addSnackbar.success({
        message: t('Sign up.Expect phone call', { phoneNumber }),
      })
    } catch (error: unknown) {
      handlePhoneError(error as ResponseError)
    }
  }

  const { data: countries = [] } = useQuery({
    queryKey: queryKeys.profile.countries(),
    queryFn: () =>
      apiClient.getCountries({
        showHiddenCountries: true,
        displayAllCountries: true,
      }),
  })

  const deleteNumber = async (number: ClientPhoneNumberDto) => {
    try {
      setIsLoading(true)
      await apiClient.deletePhoneNumber(number.id)
      await queryClient.invalidateQueries({ queryKey: ['account'] })
      refreshAccount(locale)
    } catch (e: unknown) {
      console.error(e)
      addSnackbar.error({ message: t('errors.Something went wrong! Please try again later') })
    } finally {
      setIsLoading(false)
    }
  }

  const openDeleteModal = (number: ClientPhoneNumberDto) => {
    deleteModal.open({
      phoneNumber: number,
      onConfirm: () => {
        deleteNumber(number)
        scrollIntoView([ScrollToIds.ProfilePhoneNumbersHeader])
      },
    })
  }

  const addNumber = async (data: PhoneNumberAddValues) => {
    try {
      setIsLoading(true)
      const response = await apiClient.addPhoneNumber(data)
      await queryClient.invalidateQueries({ queryKey: ['account'] })
      if (response.shouldVerify && isAccountActivatedStatus(account)) {
        await requestPhoneVerification(response)
      } else {
        refreshAccount(locale)
      }
    } catch (e: unknown) {
      console.error(e)
      addSnackbar.error({ message: t('errors.Something went wrong! Please try again later') })
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyNumber = async (
    number: ClientPhoneNumberDto,
    requestId: string,
    code: string
  ): Promise<{ success: boolean; errorCode?: string; errorMessage?: string }> => {
    try {
      await apiClient.verifyPhoneNumber(number.id, requestId, code)
      await queryClient.invalidateQueries({ queryKey: ['account'] })
      shouldCancelVerificationRef.current = false
      setVerificationSuccess(true)
      setTimeout(() => {
        setVerificationSuccess(false)
        handleCloseVerifyModal()
        refreshAccount(locale)
      }, 1000)

      return { success: true }
    } catch (error: unknown) {
      const err = error as ResponseError
      handlePhoneError(err)

      const errorCode = err?.response?.response?.data.code
      let errorMessage: string

      if (
        errorCode === 'phone_verification_check_failed' ||
        errorCode === 'max_new_verification_code_requests_reached' ||
        errorCode === 'phone_verification_max_attempts_reached'
      ) {
        errorMessage = t('Sign up.Wrong code/Phone Verification failed')
      } else {
        errorMessage = t('errors.Phone verification check has failed')
      }

      return { success: false, errorCode, errorMessage }
    }
  }

  const requestPhoneVerification = async (phoneNumber: ClientPhoneNumberDto) => {
    try {
      const response = await apiClient.requestPhoneNumberVerification(phoneNumber.id)
      const newRequestId = response.phoneVerificationId
      setRequestId(newRequestId)
      shouldCancelVerificationRef.current = true
      verifyModal.open({
        phoneNumber,
        requestId: newRequestId,
        verificationSuccess,
        resendCode,
        onReceivePhoneCall,
        onVerify: handleVerifyNumber,
        onClose: () => {
          if (shouldCancelVerificationRef.current) {
            cancelPhoneVerification(phoneNumber.id, newRequestId)
          }
        },
      })
      return response
    } catch (error: unknown) {
      handlePhoneError(error as ResponseError)
      refreshAccount(locale)
    }
  }

  const cancelPhoneVerification = async (phoneNumber: string, newRequestId: string) => {
    if (!phoneNumber || !newRequestId) {
      return
    }
    try {
      await apiClient.cancelPhoneNumberVerification(phoneNumber, newRequestId)
      await queryClient.invalidateQueries({ queryKey: ['account'] })
      handleCloseVerifyModal()
      refreshAccount(locale)
    } catch (error: unknown) {
      handlePhoneError(error as ResponseError)
    }
  }

  const makeNumberPrimary = async (number: ClientPhoneNumberDto) => {
    setIsLoading(true)
    try {
      await apiClient.updatePhoneNumber(number.id, {
        ...number,
        isPrimary: true,
      })
      await queryClient.invalidateQueries({ queryKey: ['account'] })
      refreshAccount(locale)
    } catch (e: unknown) {
      console.error(e)
      addSnackbar.error({ message: t('errors.Something went wrong! Please try again later') })
    } finally {
      setIsLoading(false)
    }
  }

  const infoMessage = useMemo(() => {
    if (!account) {
      return ''
    }
    if (filterIndividualPhoneNumbers(account).length >= 3) {
      return t('PhoneNumbers.Three phone numbers are allowed per account')
    }
    return null
  }, [account, t])

  if (isLoading) {
    return <Loading isLoading={isLoading} showLoadingIcon />
  }

  return (
    <React.Fragment>
      <PageSection
        id={ScrollToIds.ProfilePhoneNumbersHeader}
        title={t('Profile.Phone Numbers')}
        renderActions={[
          {
            onClick: () =>
              account &&
              addPhoneModal.open({
                account,
                countries,
                onConfirm: addNumber,
                onClose: () => scrollIntoView([ScrollToIds.ProfilePhoneNumbersHeader]),
              }),
            title: `${t('PhoneNumbers.Add New Phone Number')}`,
            icon: CirclePlusIcon,
            tooltip: infoMessage ? { content: infoMessage } : undefined,
            disabled: (individualPhoneNumbers.length ?? 0) >= 3,
          },
        ]}
      />
      {!individualPhoneNumbers.length ? (
        <Body1 isParagraph className='text-align-center pt-4'>
          {t('No results')}
        </Body1>
      ) : isMobile ? (
        <PhoneNumbersCard
          verifyNumber={requestPhoneVerification}
          deleteNumber={openDeleteModal}
          makeNumberPrimary={makeNumberPrimary}
          phoneNumbers={individualPhoneNumbers}
        />
      ) : (
        <PhoneNumbersTable
          isLoading={isLoading}
          verifyNumber={requestPhoneVerification}
          deleteNumber={openDeleteModal}
          makeNumberPrimary={makeNumberPrimary}
          phoneNumbers={individualPhoneNumbers}
        />
      )}
    </React.Fragment>
  )
}
